// THE SECOND VIEW — client › retainer › job, every job in its place and every charge under
// its job. Clients flow into as many columns as the screen holds, so a wide monitor shows
// several side by side instead of one card stretched across it.
//
// A client's header counts what needs attention inside it — counts of the server's own
// rows, never a figure. A client whose everything is paid is hidden unless the switch in
// the page's header is on (Collections); at the bottom of a long page that control was a
// place money went to be forgotten.
import he from '../../i18n/he';
import Money from '../../components/Money';
import EmptyState from '../../components/EmptyState';
import MonthStrip, { Legend } from './MonthStrip';
import JobBlock, { ChargeLine } from './JobBlock';
import Button from '../../components/Button';
import { isProject } from '../../components/engagement';

const t = he.collections.board;
const tr = he.moneyScreen.tree;

const isLate = (r) => r.kind === 'recorded' && r.collectible && r.overdue;
const jobCounts = (j) => ({ late: j.charges.filter(isLate).length, todo: j.toRaise.length });
const clientCounts = (c) => {
  const jobs = [...c.retainers.flatMap((r) => r.jobs), ...c.oneOffs].map(jobCounts);
  const months = c.retainers.flatMap((r) => r.card.months);
  // A one-off project's price charge (067), and a monthly retainer's extra charges.
  const ownCharges = c.retainers.flatMap((r) => [...(r.card.charges || []), ...(r.card.extras || [])]);
  return {
    late: months.filter(isLate).length + ownCharges.filter(isLate).length
      + jobs.reduce((s, j) => s + j.late, 0),
    todo: months.filter((m) => m.kind === 'expected_month' && m.gated).length
      + jobs.reduce((s, j) => s + j.todo, 0),
  };
};

// A monthly retainer's extra charges — income beyond the monthly fee — under its months.
function RetainerExtras({ card, acts }) {
  return (
    <>
      {card.extras.length > 0 && <h4 className="mp-subhead">{he.projects.extras}</h4>}
      {card.extras.map((c) => <ChargeLine key={`i${c.invoiceId}`} card={card} row={c} acts={acts} />)}
      <Button small variant="ghost" onClick={() => acts.onRecordJob(card)}>{he.projects.addExtra}</Button>
    </>
  );
}

// A one-off project's billing: its price, owed from the day the project exists (068) and
// paid here. Nothing is added to it by hand — income beyond the price comes from a job
// charging the client more than it costs us. Stands where a retainer's month strip
// stands; everything else on the block (the jobs beneath) is the retainer's own.
function ProjectBilling({ card, acts }) {
  return card.charges.map((c) => <ChargeLine key={`i${c.invoiceId}`} card={card} row={c} acts={acts} />);
}

function RetainerBlock({ node, acts }) {
  const { card } = node;
  const project = isProject(card);
  return (
    <div className="mp-retainer">
      <div className="mp-retainer-head">
        <span className="badge blue">{project ? t.kindOneOff : t.kindRetainer}</span>
        <span className="mp-retainer-title">{card.title}</span>
      </div>
      <div className="mp-figures muted">
        {card.contractValue != null && (
          <span>{project ? he.projects.price : t.contractValue} <Money value={card.contractValue} /></span>
        )}
        {card.monthlyAmount != null && <span>{t.monthly} <Money value={card.monthlyAmount} /></span>}
        {!project && card.term.openEnded && <span>{t.openEnded}</span>}
        <span>{t.invoiced} <Money value={card.invoiced} /></span>
        <span>{t.collectedTotal} <Money value={card.collected} /></span>
        {card.outstanding > 0 && <span>{t.outstanding} <strong><Money value={card.outstanding} /></strong></span>}
      </div>
      <h4 className="mp-subhead">{project ? he.projects.invoices : tr.retainerMonths}</h4>
      {project ? <ProjectBilling card={card} acts={acts} /> : (
        <>
          <MonthStrip card={card} acts={acts} />
          <RetainerExtras card={card} acts={acts} />
        </>
      )}
      {node.jobs.length > 0 && (
        <div className="mp-tree">
          <h4 className="mp-subhead">{tr.jobs}</h4>
          {node.jobs.map((j) => <JobBlock key={j.key} node={j} acts={acts} />)}
        </div>
      )}
    </div>
  );
}

function ClientBlock({ client, acts }) {
  const n = clientCounts(client);
  return (
    <section className="mp-client card">
      <header className="mp-client-head">
        <h2>{client.clientName}</h2>
        <span className="row">
          {n.late > 0 && <span className="badge red">{tr.clientLate(n.late)}</span>}
          {n.todo > 0 && <span className="badge amber">{tr.clientTodo(n.todo)}</span>}
          {n.late === 0 && n.todo === 0 && client.settled && <span className="badge green">{tr.allPaid}</span>}
        </span>
      </header>
      {client.retainers.map((r) => <RetainerBlock key={r.key} node={r} acts={acts} />)}
      {client.oneOffs.length > 0 && (
        <div className="mp-oneoffs">
          {client.retainers.length > 0 && <h4 className="mp-subhead">{tr.oneOffs}</h4>}
          {client.oneOffs.map((j) => <JobBlock key={j.key} node={j} acts={acts} />)}
        </div>
      )}
    </section>
  );
}

export default function ClientView({ tree, acts, showSettled }) {
  const shown = tree.filter((c) => showSettled || !c.settled);
  return (
    <>
      <Legend />
      {shown.length > 0
        ? <div className="mp-clients">{shown.map((c) => <ClientBlock key={c.key} client={c} acts={acts} />)}</div>
        : <EmptyState text={tr.empty} />}
    </>
  );
}
